import { z } from 'zod'
import { oc } from '@orpc/contract'
import { boardAttachmentSchema, boardItemSchema, cellValueSchema } from './boards-shared.contract'

export const boardItemCreate = oc
  .route({ method: 'POST', path: '/v2/boards/{boardId}/items' })
  .input(
    z.object({
      boardId: z.string(),
      groupId: z.string(),
      name: z.string().trim().min(1).max(200),
    }),
  )
  .output(z.object({ item: boardItemSchema }))

export const boardItemUpdate = oc
  .route({ method: 'PATCH', path: '/v2/board-items/{itemId}' })
  .input(
    z.object({
      itemId: z.string(),
      name: z.string().trim().min(1).max(200).optional(),
      groupId: z.string().optional(),
      // Partial merge into the item's values; null clears a cell.
      values: z.record(z.string(), cellValueSchema.nullable()).optional(),
      // Full replacement of the item's checklist — add, edit title, delete, or
      // reorder in a single write. An empty array clears the checklist. The
      // per-step done toggle stays on boardItemChecklistToggle for snappy
      // optimistic updates; this endpoint handles the structural edits.
      checklist: z
        .array(
          z.object({
            id: z.string(),
            title: z.string().trim().min(1).max(500),
            done: z.boolean(),
          }),
        )
        .max(50)
        .optional(),
      // Required when setting status Done on a fax-linked task (PD-1513);
      // ignored for every other task, which keeps existing completion behaviour
      // byte-for-byte unchanged.
      completionNote: z.string().trim().min(1).max(2000).optional(),
    }),
  )
  .output(z.object({ item: boardItemSchema }))

export const boardItemDelete = oc
  .route({ method: 'DELETE', path: '/v2/board-items/{itemId}' })
  .input(z.object({ itemId: z.string() }))
  .output(z.object({ id: z.string() }))

export const boardNoteAdd = oc
  .route({ method: 'POST', path: '/v2/board-items/{itemId}/notes' })
  .input(
    z.object({
      itemId: z.string(),
      text: z.string().trim().min(1).max(5000),
      // Reply to a top-level note. Absent = new top-level note.
      parentNoteId: z.string().optional(),
      // Files already uploaded to Binaries by the caller; up to 5 per update.
      attachments: z.array(boardAttachmentSchema).max(5).optional(),
    }),
  )
  .output(z.object({ item: boardItemSchema }))

// Marks a task's updates read for the calling user (stored server-side so it
// survives sign-out). Returns the caller's full read map (task id → seen time).
export const boardMarkRead = oc
  .route({ method: 'POST', path: '/v2/board-items/{itemId}/read' })
  .input(z.object({ itemId: z.string() }))
  .output(z.object({ readAt: z.record(z.string(), z.string()) }))

// Toggles a single AI-generated checklist step's done state on a board item.
export const boardItemChecklistToggle = oc
  .route({ method: 'POST', path: '/v2/board-items/{itemId}/checklist/{stepId}' })
  .input(
    z.object({
      itemId: z.string(),
      stepId: z.string(),
      done: z.boolean(),
    }),
  )
  .output(z.object({ item: boardItemSchema }))
